import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type {
  AccountStats,
  CommentReportList,
  FeedCountsList,
  PeopleStats,
  PostList,
  ReportList,
  SubmissionList,
} from '@up2d8/contracts';
import { LoadingState } from '@up2d8/ui';
import { useAdminCounts } from '../../app/zones/admin-nav';
import { formatDate, formatNumber, formatTime, uiLocale } from '../../shared/format';
import { useSession } from '../../shared/session';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  CardPad,
  Grid,
  Note,
  PageHead,
  Percent,
  Stack,
  Stats,
  Table,
  Tile,
  type AdminIconName,
} from '../admin-kit';
import { DemoToolsPanel } from '../tenants/demo-tools';

/**
 * Het startscherm van het beheerportaal (Extra 2, punt 8; sinds BHR-2 frame S1).
 *
 * Het verving de demopagina uit Stap 0, die de policy-sleutels van de aangemelde beheerder toonde
 * en een alinea "volgende stappen". Nuttig tijdens het bouwen, onbruikbaar voor een klant: wie
 * 's ochtends het portaal opent, wil weten of er iets op hem wacht. Sinds BHR-2 staat dat er ook
 * letterlijk: de kaart "Wacht op jou" rechts.
 *
 * **Alleen wat je mag zien.** Elke kaart hangt aan het recht dat de onderliggende gegevens
 * beschermt. Een kaart tonen met streepjes erin omdat het recht ontbreekt, zou verklappen dat de
 * gegevens bestaan - en dat is precies wat een recht hoort te verbergen.
 *
 * **Geen grafieken en geen trends.** Dat is Stap 33 (`/admin/reports`). Hier staan getallen van
 * vandaag, want een beginscherm dat een maandrapport nabouwt, wordt traag en wordt niet gelezen.
 */
export function AdminHomePage() {
  const { t } = useTranslation();
  const { has } = useSession();

  // Welke kolom iets te tonen heeft; een lege kolom van 360 naast de inhoud is ruis.
  const links = has('directory.person.view') || has('communication.post.draft');
  const rechts =
    has('communication.post.approve') ||
    has('floor_reports.report.handle') ||
    has('forms.submission.handle') ||
    has('interaction.comment.moderate') ||
    has('communication.post.draft') ||
    has('import.job.manage') ||
    has('directory.person.manage');

  const kolomLinks = links ? (
    <Stack>
      {has('directory.person.view') ? <PeopleCard /> : null}
      {has('communication.post.draft') ? <PostsCard /> : null}
    </Stack>
  ) : null;
  const kolomRechts = rechts ? (
    <Stack>
      <WaitingCard />
      <QuickLinks />
    </Stack>
  ) : null;

  return (
    <AdminPage testId="admin-start">
      <Greeting />
      {/* Vullen en terugzetten, alleen in een demobedrijf (ACC-3); anders bestaat de kaart niet. */}
      <DemoToolsPanel />
      {kolomLinks && kolomRechts ? (
        <Grid variant="side">
          {kolomLinks}
          {kolomRechts}
        </Grid>
      ) : (
        (kolomLinks ??
        kolomRechts ?? (
          <Note tone="info" lead={t('adminHome.nothingTitle')}>
            {t('adminHome.nothingHint')}
          </Note>
        ))
      )}
    </AdminPage>
  );
}

/**
 * "Goedemiddag, Sarah" met "Democorp · donderdag 17 september".
 *
 * De voornaam is het eerste woord van de weergavenaam: een aparte voornaam kent het account niet,
 * en "Goedemiddag, Sarah De Backer" leest als een brief. Zonder bedrijfsnaam (geen recht op de
 * huisstijl) staat alleen de datum er.
 */
function Greeting() {
  const { t } = useTranslation();
  const { profile } = useSession();
  const theme = useTenantTheme();
  const nu = new Date();
  const uur = nu.getHours();
  const groet =
    uur < 12
      ? t('beheer.start.morning')
      : uur < 18
        ? t('beheer.start.afternoon')
        : t('beheer.start.evening');
  const voornaam = profile?.displayName.trim().split(/\s+/)[0] ?? '';
  const datum = formatDate(nu, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <PageHead
      title={voornaam ? t('beheer.start.greeting', { greeting: groet, name: voornaam }) : groet}
      sub={theme?.displayName ? `${theme.displayName} · ${datum}` : datum}
    />
  );
}

/** Een link naast een kaarttitel of onder de cijfers: `.a-link` met een chevron. */
function PijlLink({ to, testId, children }: { to: string; testId?: string; children: ReactNode }) {
  return (
    <Link to={to} search={{} as never} className="a-link" data-testid={testId}>
      {children}
      <AdminIcon name="chevronRight" size={14} />
    </Link>
  );
}

/** Laden of mislukt, binnen een kaart: hetzelfde patroon voor elke kaart op dit scherm. */
function KaartStatus({ error, onRetry }: { error?: boolean; onRetry?: () => void }) {
  const { t } = useTranslation();
  if (!error) {
    return (
      <CardPad>
        <LoadingState rows={3} label={t('common.loading')} />
      </CardPad>
    );
  }
  return (
    <CardPad>
      <Note tone="danger" role="alert">
        {t('errors.generic')}
      </Note>
      {onRetry ? (
        <div>
          <Btn variant="sec" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Btn>
        </div>
      ) : null}
    </CardPad>
  );
}

/** Medewerkers: actief, uit dienst en hoeveel er al een account in gebruik hebben. */
function PeopleCard() {
  const { t } = useTranslation();
  const { api } = useSession();

  const people = useQuery({
    queryKey: ['directory', 'stats'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/people/stats');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as PeopleStats;
    },
  });

  const accounts = useQuery({
    queryKey: ['identity', 'stats'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/accounts/stats');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as AccountStats;
    },
  });

  const kop = (
    <CardHead
      title={t('adminHome.peopleTitle')}
      actions={<PijlLink to="/admin/people">{t('beheer.start.toPeople')}</PijlLink>}
    />
  );

  if (people.isPending || accounts.isPending) {
    return (
      <Card>
        {kop}
        <KaartStatus />
      </Card>
    );
  }

  if (people.isError || accounts.isError) {
    return (
      <Card>
        {kop}
        <KaartStatus
          error
          onRetry={() => {
            void people.refetch();
            void accounts.refetch();
          }}
        />
      </Card>
    );
  }

  const stats = people.data;
  const account = accounts.data;
  /*
    De noemer is "actief **en** met personeelsnummer", niet "iedereen".

    Wie geen personeelsnummer heeft, kan niet activeren - die hoort dus niet in de breuk thuis.
    Met iedereen als noemer zou de activatiegraad van een bedrijf dat nog aan het importeren is,
    altijd laag staan, en dan zegt het getal niets.
  */
  const noemer = stats?.withEmployeeNumber ?? 0;
  const geactiveerd = account?.activated ?? 0;
  const graad = noemer > 0 ? Math.round((geactiveerd / noemer) * 100) : null;

  return (
    <Card testId="admin-people-card">
      {kop}
      <CardPad>
        <Stats
          items={[
            {
              label: t('adminHome.active'),
              value: formatNumber(stats?.active ?? 0),
              sub: t('beheer.start.activeSub'),
              testId: 'stat-active',
            },
            {
              label: t('adminHome.deactivated'),
              value: formatNumber(stats?.deactivated ?? 0),
              sub: t('beheer.start.deactivatedSub'),
              testId: 'stat-deactivated',
            },
            {
              label: t('adminHome.activationRate'),
              // Geen "0%" als er niemand met een personeelsnummer is: dan is er geen breuk, en een
              // nul leest als "niemand activeerde" in plaats van "er valt niets te activeren".
              value: graad === null ? '—' : `${graad}%`,
              sub:
                graad === null
                  ? undefined
                  : t('beheer.start.activatedSub', {
                      activated: formatNumber(geactiveerd),
                      total: formatNumber(noemer),
                    }),
              testId: 'stat-activation-rate',
            },
          ]}
        />
        {(account?.pendingActivation ?? 0) > 0 ? (
          <div>
            <PijlLink to="/admin/people" testId="stat-pending-link">
              {t('adminHome.pendingActivation', { count: account?.pendingActivation ?? 0 })}
            </PijlLink>
          </div>
        ) : null}
      </CardPad>
    </Card>
  );
}

/**
 * Een datum in een lijst: "vandaag 09:12", "gisteren 14:30", "ma 14/09"; van een ander jaar de
 * volledige datum. Zo leest de mockup, en zo leest een beheerder die 's ochtends kijkt wat er
 * gisteren uitging.
 */
function lijstDatum(value: string | null, t: TFunction): string {
  if (!value) return '—';
  const datum = new Date(value);
  const nu = new Date();
  const dag = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const verschil = Math.round((dag(nu) - dag(datum)) / 86_400_000);
  if (verschil === 0) return t('beheer.start.today', { time: formatTime(datum) });
  if (verschil === 1) return t('beheer.start.yesterday', { time: formatTime(datum) });
  if (datum.getFullYear() !== nu.getFullYear()) return formatDate(datum);
  const weekdag = new Intl.DateTimeFormat(uiLocale(), { weekday: 'short' }).format(datum);
  const dd = String(datum.getDate()).padStart(2, '0');
  const mm = String(datum.getMonth() + 1).padStart(2, '0');
  return `${weekdag} ${dd}/${mm}`;
}

/** De laatste vijf gepubliceerde berichten, met hun bereik. */
function PostsCard() {
  const { t } = useTranslation();
  const { api } = useSession();

  const posts = useQuery({
    queryKey: ['communication', 'recent'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/posts', {
        params: { query: { status: 'published', limit: 5 } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as PostList).items;
    },
  });

  const ids = (posts.data ?? []).map((post) => post.id);
  /*
    Een aanroep voor alle vijf, en niet vijf keer `GET /reach/posts/:id`.

    `GET /feed/counts?postIds=` bestond al voor de tijdlijn; hem hier hergebruiken betekent ook
    dat de onderdrukking bij een kleine doelgroep (ADR-0041) automatisch klopt.
  */
  const counts = useQuery({
    queryKey: ['reach', 'counts', ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/feed/counts', {
        params: { query: { postIds: ids.join(',') } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as FeedCountsList).items;
    },
  });

  const countByPost = new Map((counts.data ?? []).map((item) => [item.postId, item]));

  return (
    <Card testId="admin-posts-card">
      <CardHead
        title={t('adminHome.postsTitle')}
        actions={<PijlLink to="/admin/posts">{t('adminHome.allPosts')}</PijlLink>}
      />
      {posts.isPending ? (
        <KaartStatus />
      ) : posts.isError ? (
        <KaartStatus error onRetry={() => void posts.refetch()} />
      ) : (posts.data?.length ?? 0) === 0 ? (
        <CardPad>
          <p className="a-muted">{t('adminHome.noPosts')}</p>
        </CardPad>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t('adminHome.post')}</th>
              <th>{t('adminHome.published')}</th>
              <th className="num">{t('adminHome.reach')}</th>
            </tr>
          </thead>
          <tbody>
            {posts.data?.map((post) => {
              const telling = countByPost.get(post.id);
              /*
                Een streepje en geen nul als de doelgroep te klein is (ADR-0041) of als het
                bericht geen bevestiging vraagt: een percentage dat niet bestaat, hoort niet als
                0% te lezen.
              */
              const percentage =
                post.requiresAcknowledgement &&
                telling &&
                !telling.suppressed &&
                telling.recipientCount
                  ? Math.round(((telling.acknowledgedCount ?? 0) / telling.recipientCount) * 100)
                  : null;
              const ontvangers = post.recipientCount;
              return (
                <tr key={post.id} data-testid="admin-post-row">
                  <td>
                    {/*
                      De rij opent het bereik van het bericht (FBK-1). Het label "Leesbevestiging"
                      is geen knop: het zegt dat dit bericht om een bevestiging vraagt.
                    */}
                    <Link
                      to="/admin/posts"
                      search={{ bereik: post.id } as never}
                      className="a-name a-link"
                      data-testid="admin-post-link"
                    >
                      {post.title}
                    </Link>
                    {post.requiresAcknowledgement || ontvangers !== null ? (
                      <div className="a-meta">
                        {post.requiresAcknowledgement ? (
                          <Badge tone="navy">
                            <AdminIcon name="check" />
                            {t('beheer.start.acknowledge')}
                          </Badge>
                        ) : null}
                        {post.requiresAcknowledgement && ontvangers !== null ? ' · ' : null}
                        {ontvangers !== null
                          ? t('beheer.start.recipients', { count: ontvangers })
                          : null}
                      </div>
                    ) : null}
                  </td>
                  <td>{lijstDatum(post.publishedAt, t)}</td>
                  <td className="num">
                    {percentage === null ? '—' : <Percent value={percentage} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

interface WachtRij {
  key: string;
  icon: AdminIconName;
  title: string;
  meta: string | null;
  to: string;
}

/**
 * "Wacht op jou" (BHR-2): wat een beheerder nu moet doen, in één kaart.
 *
 * **De tellers zijn die van de zijbalk.** `useAdminCounts` telt al voor de zijbalk; dit scherm
 * gebruikt dezelfde hook, zodat de 4 bij Aanvragen links en de 4 hier nooit uiteenlopen. Het blijft
 * de lengte van de eerste pagina van elke lijst (geen nieuw endpoint, zie deviations.md).
 *
 * De metaregel ("Oudste: verlofaanvraag van Mats Tamsin") vraagt dezelfde lijst nog eens op, onder
 * een eigen sleutel die met die van het scherm begint, en alleen als er iets wacht.
 *
 * Een rij staat er alleen met het recht én als er iets wacht; wie geen van de vier rechten heeft,
 * ziet de kaart niet.
 */
function WaitingCard() {
  const { t } = useTranslation();
  const session = useSession();
  const { api, has } = session;
  const counts = useAdminCounts(session, true);

  const rechten = {
    submissions: has('forms.submission.handle'),
    posts: has('communication.post.approve'),
    floorReports: has('floor_reports.report.handle'),
    moderation: has('interaction.comment.moderate'),
  };
  // Zonder recht telt een rij niet mee, ook niet als "nog niet geladen".
  const aantal = {
    submissions: rechten.submissions ? counts.submissions : 0,
    posts: rechten.posts ? counts.posts : 0,
    floorReports: rechten.floorReports ? counts.floorReports : 0,
    moderation: rechten.moderation ? counts.moderation : 0,
  };
  const canManageReports = has('floor_reports.report.manage');

  const aanvragen = useQuery({
    queryKey: ['forms', 'inbox', 'start'],
    enabled: (aantal.submissions ?? 0) > 0,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/forms/submissions', {
        params: { query: { status: 'submitted' } },
      });
      return (data as SubmissionList | undefined)?.items ?? [];
    },
  });

  const berichten = useQuery({
    queryKey: ['posts', 'pending-count', 'start'],
    enabled: (aantal.posts ?? 0) > 0,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/posts', {
        params: { query: { status: 'pending_approval', limit: 100 } as never },
      });
      return (data as PostList | undefined)?.items ?? [];
    },
  });

  const meldingen = useQuery({
    queryKey: ['floor-reports', 'open-count', canManageReports, 'start'],
    enabled: (aantal.floorReports ?? 0) > 0,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/floor-reports', {
        params: {
          query: { status: 'new', ...(canManageReports ? { all: 'true' as const } : {}) },
        },
      });
      return (data as ReportList | undefined)?.items ?? [];
    },
  });

  const moderatie = useQuery({
    queryKey: ['moderation', 'start'],
    enabled: (aantal.moderation ?? 0) > 0,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/moderation/comments', {});
      return (data as CommentReportList | undefined)?.items ?? [];
    },
  });

  if (!Object.values(rechten).some(Boolean)) return null;

  const rijen: WachtRij[] = [];

  if ((aantal.submissions ?? 0) > 0) {
    const oudste = [...(aanvragen.data ?? [])].sort((a, b) =>
      a.submittedAt.localeCompare(b.submittedAt),
    )[0];
    rijen.push({
      key: 'submissions',
      icon: 'inbox',
      title: t('beheer.start.waitSubmissions', { count: aantal.submissions }),
      meta: oudste
        ? t('beheer.start.waitSubmissionsMeta', {
            form: oudste.formTitle,
            name: oudste.personName,
            date: lijstDatum(oudste.submittedAt, t),
          })
        : null,
      to: '/admin/aanvragen',
    });
  }

  if ((aantal.posts ?? 0) > 0) {
    const namen = [
      ...new Set((berichten.data ?? []).map((post) => post.submittedByName).filter(Boolean)),
    ] as string[];
    rijen.push({
      key: 'posts',
      icon: 'megaphone',
      title: t('beheer.start.waitPosts', { count: aantal.posts }),
      meta:
        namen.length === 0
          ? null
          : namen.length <= 2
            ? t('beheer.start.waitPostsMeta', {
                names: new Intl.ListFormat(uiLocale(), { type: 'conjunction' }).format(namen),
              })
            : t('beheer.start.waitPostsMetaMore', { name: namen[0], count: namen.length - 1 }),
      to: '/admin/posts',
    });
  }

  if ((aantal.floorReports ?? 0) > 0) {
    const nieuwste = [...(meldingen.data ?? [])].sort((a, b) =>
      b.submittedAt.localeCompare(a.submittedAt),
    )[0];
    rijen.push({
      key: 'floor-reports',
      icon: 'alert',
      title: t('beheer.start.waitReports', { count: aantal.floorReports }),
      meta: nieuwste
        ? nieuwste.zoneName
          ? t('beheer.start.waitReportsMetaZone', {
              type: nieuwste.typeName,
              zone: nieuwste.zoneName,
            })
          : t('beheer.start.waitReportsMeta', { type: nieuwste.typeName })
        : null,
      to: '/admin/meldingen',
    });
  }

  // Niet in de mockup, wel een teller in de zijbalk: een gerapporteerde reactie wacht net zo goed.
  if ((aantal.moderation ?? 0) > 0) {
    const laatste = [...(moderatie.data ?? [])].sort((a, b) =>
      b.lastReportedAt.localeCompare(a.lastReportedAt),
    )[0];
    rijen.push({
      key: 'moderation',
      icon: 'flag',
      title: t('beheer.start.waitModeration', { count: aantal.moderation }),
      meta: laatste ? t('beheer.start.waitModerationMeta', { title: laatste.postTitle }) : null,
      to: '/admin/moderatie',
    });
  }

  const waarden = Object.values(aantal);
  const totaal = waarden.reduce<number>((som, waarde) => som + (waarde ?? 0), 0);
  const geladen = waarden.every((waarde) => waarde !== undefined);

  return (
    <Card testId="admin-waiting-card">
      <CardHead
        title={t('beheer.start.waitTitle')}
        count={totaal > 0 ? totaal : undefined}
        countAttention
      />
      {rijen.length > 0 ? (
        rijen.map((rij) => (
          <div key={rij.key} className="a-item" data-testid={`waiting-${rij.key}`}>
            <Tile tone="orange">
              <AdminIcon name={rij.icon} size={20} />
            </Tile>
            <div className="a-item-info">
              <div className="a-name" id={`wacht-${rij.key}`}>
                {rij.title}
              </div>
              {rij.meta ? <div className="a-meta">{rij.meta}</div> : null}
            </div>
            {/* De titel als beschrijving: vier keer "Openen" is voor een schermlezer anders vier
                keer dezelfde link. Niet in de naam, die blijft "Openen" (zoals bij de tellers in de
                zijbalk). */}
            <Link
              to={rij.to}
              search={{} as never}
              className="a-link"
              aria-describedby={`wacht-${rij.key}`}
            >
              {t('beheer.start.open')}
            </Link>
          </div>
        ))
      ) : geladen ? (
        <CardPad>
          <p className="a-muted">{t('beheer.start.waitNothing')}</p>
        </CardPad>
      ) : (
        <KaartStatus />
      )}
    </Card>
  );
}

/**
 * Ga naar: de drie dingen die een beheerder het vaakst komt doen.
 *
 * De opschriften zijn **handelingen** en herhalen niet de naam van het menu-item ernaast
 * ("Personeelslijst opladen", niet "Importeren"). Dat leest beter op een knop, en het voorkomt dat
 * er op dit scherm twee links staan die hetzelfde heten - verwarrend voor een schermlezer, en voor
 * elke test die op een naam zoekt.
 */
function QuickLinks() {
  const { t } = useTranslation();
  const { has } = useSession();

  const links = [
    has('communication.post.draft')
      ? {
          to: '/admin/posts',
          label: t('adminHome.quickPost'),
          meta: t('beheer.start.quickPostMeta'),
          icon: <AdminIcon name="pen" size={20} />,
        }
      : null,
    has('import.job.manage')
      ? {
          to: '/admin/imports',
          label: t('beheer.start.quickImport'),
          meta: t('beheer.start.quickImportMeta'),
          icon: <AdminIcon name="upload" size={20} />,
        }
      : null,
    has('directory.person.manage')
      ? {
          to: '/admin/people',
          label: t('adminHome.quickPerson'),
          meta: t('beheer.start.quickPersonMeta'),
          icon: <AdminIcon name="plus" size={20} />,
        }
      : null,
  ].filter((link): link is NonNullable<typeof link> => link !== null);

  if (links.length === 0) return null;

  return (
    <Card testId="admin-quick-links">
      <CardHead title={t('beheer.start.quickTitle')} />
      <CardPad>
        {links.map((link) => (
          <Link key={link.to} to={link.to} search={{} as never} className="a-orgbtn">
            <Tile>{link.icon}</Tile>
            <span style={{ flex: 1 }}>
              <span className="a-name" style={{ display: 'block' }}>
                {link.label}
              </span>
              <span className="a-meta">{link.meta}</span>
            </span>
            <AdminIcon name="chevronRight" size={18} style={{ color: 'var(--a-muted)' }} />
          </Link>
        ))}
      </CardPad>
    </Card>
  );
}
